import { DEMO_FIELD_TYPE, DEMO_CALCULATOR_STEP } from 'config/constants'
import { centerDemoField } from '../../center-field'
import {
  DEMO_DELIVERY_DATE_CHOOSE_EVENT,
  DEMO_DELIVERY_DATE_OPEN_EVENT,
  DEMO_DELIVERY_FIELD_NAMES,
  DEMO_DELIVERY_LOCATION_CHOOSE_EVENT,
  DEMO_DELIVERY_LOCATION_SEARCH_EVENT,
  DEMO_SCROLL_DELAY,
  DEMO_STEP_PLAYBACK_COMPLETE_EVENT,
} from '../../playback-constants'
import {
  animateAction,
  wait,
  waitForDocumentElement,
  waitForDocumentElementToDisappear,
  waitForEnabledDocumentElement,
  waitForStableElementPosition,
  scrollToDemoPlayerElement,
} from '../../helpers'
import { playDemoFields } from '../../play-fields'
import { scrollToDemoRoute } from './scroll-to-route'

const getLocationField = (
  input: HTMLInputElement,
  targetValue: string,
  fieldName: string,
  controls: PlaybackControls,
): DemoPlaybackField => ({
  element: input,
  name: fieldName,
  onChoose: () => controls.dispatchEvent(new CustomEvent(DEMO_DELIVERY_LOCATION_CHOOSE_EVENT, {
    detail: { fieldName },
  })),
  onType: (value) => controls.dispatchEvent(new CustomEvent(DEMO_DELIVERY_LOCATION_SEARCH_EVENT, {
    detail: {
      fieldName,
      query: value,
      shouldOpen: value === targetValue,
    },
  })),
  optionSelector: `[data-demo-delivery-location-option="${fieldName}"]`,
  shouldScroll: false,
  type: DEMO_FIELD_TYPE.AUTOCOMPLETE,
  value: targetValue,
})

export const playDeliveryStep = async ({
  controls,
  fields,
  onComplete,
  stepElement,
}: DeliveryStepParams): Promise<boolean> => {
  const searchForm = stepElement.querySelector<HTMLFormElement>('[data-demo-delivery-search]')
  const locationFromInput = searchForm?.querySelector<HTMLInputElement>('#location_from input')
  const locationToInput = searchForm?.querySelector<HTMLInputElement>('#location_to input')

  const locationFromName = searchForm?.dataset.demoLocationFromName || ''
  const locationToName = searchForm?.dataset.demoLocationToName || ''
  const locationFromId = searchForm?.dataset.demoLocationFromId || ''
  const locationToId = searchForm?.dataset.demoLocationToId || ''
  const plannedShipmentDate = searchForm?.dataset.demoPlannedShipmentDate || ''

  const hasLocationNames = Boolean(locationFromName && locationToName)
  const hasLocationInputs = locationFromInput && locationToInput

  if (!hasLocationNames || !hasLocationInputs) {
    return false
  }

  const locationFieldsAreReady = await Promise.all([
    DEMO_DELIVERY_FIELD_NAMES.locationFrom,
    DEMO_DELIVERY_FIELD_NAMES.locationTo,
  ].map(fieldName => waitForDocumentElement(
    `[data-demo-delivery-location="${fieldName}"][data-demo-playback-ready="true"]`,
    controls.isCancelled,
  )))

  if (locationFieldsAreReady.some(field => !field)) {
    return false
  }

  if (!centerDemoField(locationFromInput)) {
    locationFromInput.scrollIntoView({ behavior: 'auto', block: 'center' })
  }

  if (!await waitForStableElementPosition(locationFromInput, controls.isCancelled)) {
    return false
  }

  const hasTypedLocationFrom = await playDemoFields({
    controls,
    fields: [ getLocationField(
      locationFromInput,
      locationFromName,
      DEMO_DELIVERY_FIELD_NAMES.locationFrom,
      controls,
    ) ],
    step: DEMO_CALCULATOR_STEP.DELIVERY,
  })

  if (!hasTypedLocationFrom) {
    return false
  }

  if (!centerDemoField(locationToInput)) {
    locationToInput.scrollIntoView({ behavior: 'auto', block: 'center' })
  }

  if (!await waitForStableElementPosition(locationToInput, controls.isCancelled)) {
    return false
  }

  const hasTypedLocationTo = await playDemoFields({
    controls,
    fields: [ getLocationField(
      locationToInput,
      locationToName,
      DEMO_DELIVERY_FIELD_NAMES.locationTo,
      controls,
    ) ],
    step: DEMO_CALCULATOR_STEP.DELIVERY,
  })

  if (!hasTypedLocationTo) {
    return false
  }

  const hasLocationIds = Boolean(locationFromId && locationToId)
  const hasDeliveryData = hasLocationIds && Boolean(plannedShipmentDate)

  if (!hasDeliveryData) {
    return false
  }

  const plannedShipmentDateField = searchForm?.querySelector<HTMLElement>(
    `#${DEMO_DELIVERY_FIELD_NAMES.plannedShipmentDate}`,
  )

  if (!plannedShipmentDateField) {
    return false
  }

  const hasChosenDate = await playDemoFields({
    controls,
    fields: [
      {
        element: plannedShipmentDateField,
        name: DEMO_DELIVERY_FIELD_NAMES.plannedShipmentDate,
        onChoose: () => controls.dispatchEvent(new CustomEvent(DEMO_DELIVERY_DATE_CHOOSE_EVENT)),
        onOpen: () => controls.dispatchEvent(new CustomEvent(DEMO_DELIVERY_DATE_OPEN_EVENT)),
        type: DEMO_FIELD_TYPE.DATE,
        value: plannedShipmentDate,
      },
    ],
    step: DEMO_CALCULATOR_STEP.DELIVERY,
  })

  if (!hasChosenDate) {
    return false
  }

  const searchAction = await waitForEnabledDocumentElement(
    '[data-calculator-step="delivery"] [data-demo-playback-action="search-delivery"]',
    controls.isCancelled,
  )

  if (!searchAction) {
    return false
  }

  await animateAction(searchAction)
  controls.setActiveFieldElement(null)
  searchAction.click()

  const chooseRouteAction = await waitForDocumentElement(
    '[data-demo-playback-action="choose-delivery-route"]',
    controls.isCancelled,
  )

  if (!chooseRouteAction) {
    return false
  }

  const isEmbeddedModal = Boolean(chooseRouteAction.closest('.demo-player__modal-layer'))

  if (isEmbeddedModal) {
    const reachedRoute = await scrollToDemoRoute({ action: chooseRouteAction, isCancelled: controls.isCancelled })

    if (!reachedRoute) {
      return false
    }
  }

  await animateAction(chooseRouteAction, !isEmbeddedModal)

  if (controls.isCancelled() || !chooseRouteAction.isConnected) {
    return false
  }

  chooseRouteAction.click()

  await waitForDocumentElementToDisappear(
    '[data-demo-playback-action="choose-delivery-route"]',
    controls.isCancelled,
  )
  await waitForDocumentElementToDisappear('[role="dialog"]', controls.isCancelled)

  const completedDelivery = await waitForDocumentElement(
    '[data-calculator-step="delivery"] .completed',
    controls.isCancelled,
  )

  if (!completedDelivery) {
    return false
  }

  const deliveryChoice = await waitForDocumentElement(
    '[data-demo-playback-target="delivery-choice"]',
    controls.isCancelled,
  )

  if (!deliveryChoice) {
    return false
  }

  const scrolledInPlayer = await scrollToDemoPlayerElement({ element: deliveryChoice, isCancelled: controls.isCancelled })

  if (controls.isCancelled()) {
    return false
  }

  if (!scrolledInPlayer && !centerDemoField(deliveryChoice)) {
    deliveryChoice.scrollIntoView({ behavior: 'smooth', block: 'center' })
    await wait(DEMO_SCROLL_DELAY)
  }

  controls.dispatchEvent(new CustomEvent(DEMO_STEP_PLAYBACK_COMPLETE_EVENT, {
    detail: { step: DEMO_CALCULATOR_STEP.DELIVERY },
  }))
  onComplete()
  controls.clearFields(fields)

  return true
}
